import { alpha, createTheme, lighten, darken } from '@mui/material';
import '@mui/lab/themeAugmentation';

const PRIMARY = '#2D5BFF';
const PRIMARY_DARK = '#1E3FCC';
const PRIMARY_LIGHT = '#5C82FF';

const themeColors = {
  primary: PRIMARY,
  secondary: '#6E759F',
  success: '#16A34A',
  warning: '#F59E0B',
  error: '#DC2626',
  info: '#0EA5E9',
  black: '#0F172A',
  white: '#FFFFFF',
  primaryAlt: '#0F172A',
};

const SURFACE = {
  page: '#F7F8FB',
  paper: '#FFFFFF',
  subtle: '#EEF1F8',
  border: '#E5E7EE',
};

const TEXT = {
  primary: '#0F172A',
  secondary: '#475569',
  disabled: '#94A3B8',
};

const colors = {
  gradients: {
    blue1: 'linear-gradient(135deg, #6B73FF 0%, #000DFF 100%)',
    blue2: 'linear-gradient(135deg, #ABDCFF 0%, #0396FF 100%)',
    blue3: 'linear-gradient(127.55deg, #141E30 3.73%, #243B55 92.26%)',
    blue4: 'linear-gradient(-20deg, #2b5876 0%, #4e4376 100%)',
    blue5: 'linear-gradient(135deg, #97ABFF 10%, #123597 100%)',
    orange1: 'linear-gradient(135deg, #FCCF31 0%, #F55555 100%)',
    orange2: 'linear-gradient(135deg, #FFD3A5 0%, #FD6585 100%)',
    orange3: 'linear-gradient(120deg, #f6d365 0%, #fda085 100%)',
    purple1: 'linear-gradient(135deg, #43CBFF 0%, #9708CC 100%)',
    purple3: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    pink1: 'linear-gradient(135deg, #F6CEEC 0%, #D939CD 100%)',
    pink2: 'linear-gradient(135deg, #F761A1 0%, #8C1BAB 100%)',
    green1: 'linear-gradient(135deg, #FFF720 0%, #3CD500 100%)',
    green2: 'linear-gradient(to bottom, #00b09b, #96c93d)',
    black1: 'linear-gradient(100.66deg, #434343 6.56%, #000000 93.57%)',
    black2: 'linear-gradient(60deg, #29323c 0%, #485563 100%)',
  },
  shadows: {
    success:
      '0px 1px 4px rgba(22, 163, 74, 0.20), 0px 3px 12px 2px rgba(22, 163, 74, 0.18)',
    error:
      '0px 1px 4px rgba(220, 38, 38, 0.20), 0px 3px 12px 2px rgba(220, 38, 38, 0.18)',
    info: '0px 1px 4px rgba(14, 165, 233, 0.20), 0px 3px 12px 2px rgba(14, 165, 233, 0.18)',
    primary:
      '0px 1px 4px rgba(45, 91, 255, 0.20), 0px 3px 12px 2px rgba(45, 91, 255, 0.18)',
    warning:
      '0px 1px 4px rgba(245, 158, 11, 0.20), 0px 3px 12px 2px rgba(245, 158, 11, 0.18)',
    card: '0px 1px 2px rgba(15, 23, 42, 0.04), 0px 1px 3px rgba(15, 23, 42, 0.06)',
    cardSm: '0px 1px 2px rgba(15, 23, 42, 0.04)',
    cardLg:
      '0px 8px 24px -4px rgba(15, 23, 42, 0.10), 0px 4px 8px -2px rgba(15, 23, 42, 0.06)',
  },
  layout: {
    general: { bodyBg: SURFACE.page },
    sidebar: {
      background: themeColors.white,
      textColor: themeColors.secondary,
      dividerBg: SURFACE.border,
      menuItemColor: TEXT.primary,
      menuItemColorActive: PRIMARY,
      menuItemBg: themeColors.white,
      menuItemBgActive: SURFACE.subtle,
      menuItemIconColor: TEXT.secondary,
      menuItemIconColorActive: PRIMARY,
      menuItemHeadingColor: darken(themeColors.secondary, 0.3),
    },
  },
  alpha: {
    white: {
      5: alpha(themeColors.white, 0.02),
      10: alpha(themeColors.white, 0.1),
      30: alpha(themeColors.white, 0.3),
      50: alpha(themeColors.white, 0.5),
      70: alpha(themeColors.white, 0.7),
      100: themeColors.white,
    },
    trueWhite: {
      5: alpha(themeColors.white, 0.02),
      10: alpha(themeColors.white, 0.1),
      30: alpha(themeColors.white, 0.3),
      50: alpha(themeColors.white, 0.5),
      70: alpha(themeColors.white, 0.7),
      100: themeColors.white,
    },
    black: {
      5: alpha(themeColors.black, 0.02),
      10: alpha(themeColors.black, 0.1),
      30: alpha(themeColors.black, 0.3),
      50: alpha(themeColors.black, 0.5),
      70: alpha(themeColors.black, 0.7),
      100: themeColors.black,
    },
  },
  secondary: {
    lighter: alpha(themeColors.secondary, 0.1),
    light: lighten(themeColors.secondary, 0.3),
    main: themeColors.secondary,
    dark: darken(themeColors.secondary, 0.2),
  },
  primary: {
    lighter: alpha(PRIMARY, 0.1),
    light: PRIMARY_LIGHT,
    main: PRIMARY,
    dark: PRIMARY_DARK,
  },
  success: {
    lighter: alpha(themeColors.success, 0.1),
    light: lighten(themeColors.success, 0.3),
    main: themeColors.success,
    dark: darken(themeColors.success, 0.2),
  },
  warning: {
    lighter: alpha(themeColors.warning, 0.1),
    light: lighten(themeColors.warning, 0.3),
    main: themeColors.warning,
    dark: darken(themeColors.warning, 0.2),
  },
  error: {
    lighter: alpha(themeColors.error, 0.1),
    light: lighten(themeColors.error, 0.3),
    main: themeColors.error,
    dark: darken(themeColors.error, 0.2),
  },
  info: {
    lighter: alpha(themeColors.info, 0.1),
    light: lighten(themeColors.info, 0.3),
    main: themeColors.info,
    dark: darken(themeColors.info, 0.2),
  },
};

export const SnapAssessTheme = createTheme({
  colors: {
    gradients: colors.gradients,
    shadows: colors.shadows,
    alpha: colors.alpha,
    secondary: colors.secondary,
    primary: colors.primary,
    success: colors.success,
    warning: colors.warning,
    error: colors.error,
    info: colors.info,
  },
  general: {
    reactFrameworkColor: '#00D8FF',
    borderRadiusSm: '6px',
    borderRadius: '10px',
    borderRadiusLg: '12px',
    borderRadiusXl: '16px',
    deleteModalIcon: '#434469',
    deleteModalIconBackgroundBolor: 'rgba(169, 169, 209, 0.4)',
  },
  sidebar: {
    background: colors.layout.sidebar.background,
    textColor: colors.layout.sidebar.textColor,
    dividerBg: colors.layout.sidebar.dividerBg,
    menuItemColor: colors.layout.sidebar.menuItemColor,
    menuItemColorActive: colors.layout.sidebar.menuItemColorActive,
    menuItemBg: colors.layout.sidebar.menuItemBg,
    menuItemBgActive: colors.layout.sidebar.menuItemBgActive,
    menuItemIconColor: colors.layout.sidebar.menuItemIconColor,
    menuItemIconColorActive: colors.layout.sidebar.menuItemIconColorActive,
    menuItemHeadingColor: colors.layout.sidebar.menuItemHeadingColor,
    boxShadow: colors.shadows.cardSm,
    width: '252px',
  },
  header: {
    height: '64px',
    background: colors.alpha.white[100],
    boxShadow: colors.shadows.cardSm,
    textColor: colors.secondary.main,
  },
  spacing: 8,
  shape: { borderRadius: 12 },
  palette: {
    mode: 'light',
    common: {
      black: themeColors.black,
      white: themeColors.white,
    },
    primary: {
      light: PRIMARY_LIGHT,
      main: PRIMARY,
      dark: PRIMARY_DARK,
      contrastText: '#FFFFFF',
    },
    secondary: {
      light: colors.secondary.light,
      main: colors.secondary.main,
      dark: colors.secondary.dark,
    },
    error: {
      light: colors.error.light,
      main: colors.error.main,
      dark: colors.error.dark,
      contrastText: '#FFFFFF',
    },
    success: {
      light: colors.success.light,
      main: colors.success.main,
      dark: colors.success.dark,
      contrastText: '#FFFFFF',
    },
    info: {
      light: colors.info.light,
      main: colors.info.main,
      dark: colors.info.dark,
      contrastText: '#FFFFFF',
    },
    warning: {
      light: colors.warning.light,
      main: colors.warning.main,
      dark: colors.warning.dark,
      contrastText: '#FFFFFF',
    },
    text: {
      primary: TEXT.primary,
      secondary: TEXT.secondary,
      disabled: TEXT.disabled,
    },
    background: {
      paper: SURFACE.paper,
      default: SURFACE.page,
    },
    divider: SURFACE.border,
    action: {
      active: TEXT.primary,
      hover: SURFACE.subtle,
      hoverOpacity: 0.06,
      selected: alpha(PRIMARY, 0.08),
      selectedOpacity: 0.08,
      disabled: TEXT.disabled,
      disabledBackground: SURFACE.subtle,
      disabledOpacity: 0.38,
      focus: alpha(PRIMARY, 0.12),
      focusOpacity: 0.12,
      activatedOpacity: 0.12,
    },
  },
  breakpoints: {
    values: { xs: 0, sm: 600, md: 960, lg: 1280, xl: 1840 },
  },
  typography: {
    fontFamily:
      '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
    h1: { fontWeight: 700, fontSize: '2rem', letterSpacing: '-0.02em' },
    h2: { fontWeight: 700, fontSize: '1.75rem', letterSpacing: '-0.02em' },
    h3: { fontWeight: 700, fontSize: '1.375rem', letterSpacing: '-0.01em' },
    h4: {
      fontWeight: 700,
      fontSize: '1.5rem',
      letterSpacing: '-0.01em',
      lineHeight: 1.3,
    },
    h5: { fontWeight: 700, fontSize: '1.125rem', lineHeight: 1.3 },
    h6: { fontWeight: 700, fontSize: '1rem', lineHeight: 1.4 },
    subtitle1: { fontWeight: 600, fontSize: '0.9375rem', color: TEXT.primary },
    subtitle2: { fontWeight: 500, fontSize: '0.875rem', color: TEXT.secondary },
    body1: { fontSize: '0.9375rem', lineHeight: 1.55 },
    body2: { fontSize: '0.875rem', lineHeight: 1.55, color: TEXT.secondary },
    button: { fontWeight: 600, textTransform: 'none', letterSpacing: 0 },
    caption: {
      fontSize: '0.75rem',
      fontWeight: 500,
      textTransform: 'none',
      color: TEXT.secondary,
      letterSpacing: 0,
    },
    overline: {
      fontSize: '0.75rem',
      fontWeight: 700,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        'html, body': { width: '100%', height: '100%' },
        body: {
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100%',
          width: '100%',
          flex: 1,
          backgroundColor: SURFACE.page,
          color: TEXT.primary,
        },
        '#root': {
          width: '100%',
          height: '100%',
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
        },
        html: {
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100%',
          width: '100%',
          MozOsxFontSmoothing: 'grayscale',
          WebkitFontSmoothing: 'antialiased',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { padding: 0, backgroundImage: 'none' },
        elevation0: { boxShadow: 'none' },
        outlined: {
          boxShadow: 'none',
          border: `1px solid ${SURFACE.border}`,
        },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0, variant: 'outlined' },
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: `1px solid ${SURFACE.border}`,
          backgroundColor: SURFACE.paper,
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: 24,
          '&:last-child': { paddingBottom: 24 },
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true, disableRipple: true },
      styleOverrides: {
        root: {
          fontWeight: 600,
          textTransform: 'none',
          borderRadius: 8,
          paddingLeft: 16,
          paddingRight: 16,
        },
        sizeSmall: { padding: '6px 12px', fontSize: '0.8125rem' },
        sizeMedium: { padding: '8px 16px', fontSize: '0.875rem' },
        sizeLarge: { padding: '10px 20px', fontSize: '0.9375rem' },
        containedPrimary: { '&:hover': { backgroundColor: PRIMARY_DARK } },
      },
    },
    MuiIconButton: {
      styleOverrides: { root: { borderRadius: 8 } },
    },
    MuiChip: {
      styleOverrides: { root: { fontWeight: 600, borderRadius: 6 } },
    },
    MuiAppBar: {
      defaultProps: { elevation: 0, color: 'inherit' },
      styleOverrides: {
        root: {
          backgroundColor: SURFACE.paper,
          color: TEXT.primary,
          borderBottom: `1px solid ${SURFACE.border}`,
          boxShadow: 'none',
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: '64px !important',
          paddingLeft: '16px !important',
          paddingRight: '16px !important',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          backgroundColor: SURFACE.border,
          borderColor: SURFACE.border,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottomColor: SURFACE.border,
          fontSize: '0.875rem',
          color: TEXT.primary,
        },
        head: {
          textTransform: 'none',
          fontSize: '0.75rem',
          fontWeight: 700,
          letterSpacing: '0.04em',
          color: TEXT.secondary,
          backgroundColor: SURFACE.subtle,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        head: { backgroundColor: SURFACE.subtle },
        root: {
          '&.MuiTableRow-hover:hover': {
            backgroundColor: alpha(PRIMARY, 0.04),
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundColor: SURFACE.paper,
        },
      },
    },
    MuiAlert: {
      styleOverrides: { root: { borderRadius: 8 } },
    },
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          h1: 'h1',
          h2: 'h2',
          h3: 'div',
          h4: 'div',
          h5: 'div',
          h6: 'div',
          subtitle1: 'div',
          subtitle2: 'div',
          body1: 'div',
          body2: 'div',
        },
      },
      styleOverrides: {
        gutterBottom: { marginBottom: 8 },
      },
    },
  },
  shadows: [
    'none',
    colors.shadows.cardSm,
    colors.shadows.card,
    colors.shadows.card,
    colors.shadows.card,
    colors.shadows.card,
    colors.shadows.cardLg,
    colors.shadows.cardLg,
    colors.shadows.cardLg,
    colors.shadows.cardLg,
    colors.shadows.cardLg,
    colors.shadows.cardLg,
    colors.shadows.cardLg,
    colors.shadows.cardLg,
    colors.shadows.cardLg,
    colors.shadows.cardLg,
    colors.shadows.cardLg,
    colors.shadows.cardLg,
    colors.shadows.cardLg,
    colors.shadows.cardLg,
    colors.shadows.cardLg,
    colors.shadows.cardLg,
    colors.shadows.cardLg,
    colors.shadows.cardLg,
    colors.shadows.cardLg,
  ],
});
